import {
  IQuoteOfferingRemoveReqSchema,
  IQuoteOfferingReqSchema,
  IQuoteOfferingRespSchema,
  IQuoteRespSchema,
} from '@monetize/types/app';
import { useQueryClient } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { useCallback, useState } from 'react';
import { handleApiErrorToast } from '../api/axios';
import {
  cpqServiceQueryKeys,
  doCreateQuoteOffering,
  doRemoveQuoteOffering,
  doRevertQuoteOffering,
  doUpdateQuoteOffering,
} from '../api/cpqService';
import { enqueueForQuote } from '../api/quoteWriteChain';
import { logger } from '../services/logger';
import {
  QuoteOfferingLoadingPromise,
  quoteOfferingLoadingPromises,
} from '../store/quote.store';

const useQuoteOffering = (quoteId: string) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [createOfferingLoading, setCreateOfferingLoading] =
    useState<boolean>(false);
  const [quoteOffering, setQuoteOffering] =
    useState<IQuoteOfferingRespSchema | null>(null);
  const queryClient = useQueryClient();

  /**
   * Read synchronously so `updateQuoteOffering` reserves its place on the quote
   * chain in the tick it is called. An `await` here would let a status
   * transition requested right after it enter the chain first.
   */
  const getLoadingPromises = useAtomCallback(
    useCallback((get) => get(quoteOfferingLoadingPromises), []),
  );
  const setLoadingPromises = useSetAtom(quoteOfferingLoadingPromises);

  /**
   * Serialize all offering mutations per quoteId via the quote write chain,
   * and surface in-flight work to the Jotai atom so the duplicate-suppression
   * check in `updateQuoteOffering` can see deletes and reverts still queued.
   */
  const performAsyncAction = async <T>(
    context: Omit<QuoteOfferingLoadingPromise, 'promise'>,
    asyncFn: () => Promise<T>,
  ): Promise<T | null> => {
    let resolveVisible!: () => void;
    const visiblePromise = new Promise<void>((resolve) => {
      resolveVisible = resolve;
    });
    const functionWithContext: QuoteOfferingLoadingPromise = {
      ...context,
      promise: visiblePromise,
    };
    setLoadingPromises((prev) => [...prev, functionWithContext]);

    try {
      return await enqueueForQuote<T | null>(quoteId, async () => {
        try {
          return await asyncFn();
        } catch (ex) {
          logger.warn('Error performing async quote offering action', ex);
          return null;
        }
      });
    } finally {
      resolveVisible();
      setLoadingPromises((prev) =>
        prev.filter(
          (loadingPromise) => loadingPromise.promise !== visiblePromise,
        ),
      );
    }
  };

  const createQuoteOffering = async (
    data: IQuoteOfferingReqSchema,
  ): Promise<IQuoteOfferingRespSchema | null> => {
    return performAsyncAction<IQuoteOfferingRespSchema | null>(
      {
        operation: 'create',
        quoteId,
      },
      async () => {
        try {
          setCreateOfferingLoading(true);
          const res = await doCreateQuoteOffering(quoteId, data);
          setQuoteOffering(res);

          queryClient.invalidateQueries({
            queryKey: [...cpqServiceQueryKeys.quotePrices(quoteId)],
          });
          return res;
        } catch (error) {
          handleApiErrorToast(error);
          return null;
        } finally {
          setCreateOfferingLoading(false);
        }
      },
    );
  };

  const updateQuoteOffering = async (
    id: string,
    data: IQuoteOfferingReqSchema,
  ): Promise<IQuoteOfferingRespSchema | null> => {
    if (
      getLoadingPromises().some(
        (loadingPromise) =>
          (loadingPromise.operation === 'delete' ||
            loadingPromise.operation === 'revert') &&
          loadingPromise.quoteOfferingId === id,
      )
    ) {
      return null;
    }

    return performAsyncAction<IQuoteOfferingRespSchema | null>(
      {
        operation: 'update',
        quoteId,
        quoteOfferingId: id,
      },
      async () => {
        try {
          setLoading(true);
          const res = await doUpdateQuoteOffering(quoteId, id, data);
          setQuoteOffering(res);

          queryClient.invalidateQueries({
            queryKey: [
              ...cpqServiceQueryKeys.quotePrices(quoteId),
              ...cpqServiceQueryKeys.quotesById(quoteId),
            ],
          });
          return res;
        } catch (error) {
          handleApiErrorToast(error);
          return null;
        } finally {
          setLoading(false);
        }
      },
    );
  };

  const revertQuoteOffering = async (id: string) => {
    return performAsyncAction<IQuoteRespSchema | null>(
      {
        operation: 'revert',
        quoteId,
        quoteOfferingId: id,
      },
      async () => {
        try {
          setLoading(true);
          const res = await doRevertQuoteOffering(quoteId, id);

          queryClient.invalidateQueries({
            queryKey: [...cpqServiceQueryKeys.quotePrices(quoteId)],
          });
          /**
           * Reverting replaces the offering with a brand new one, so any group
           * that held it now lists an id the quote no longer has. Containers
           * render from that list (see findQuoteOfferingGroupForOffering), so
           * without this the reverted offering shows up as ungrouped until
           * something else refetched the groups — the backend has already
           * re-pointed the group at the replacement.
           *
           * Awaited so the refreshed membership is in the cache before the
           * caller puts the reverted quote into state: the two then land in
           * one render, instead of the offering visibly jumping out of its
           * group and back in.
           */
          await queryClient.invalidateQueries({
            queryKey: [...cpqServiceQueryKeys.quoteOfferingGroups(quoteId)],
          });
          return res;
        } catch (error) {
          handleApiErrorToast(error);
          return null;
        } finally {
          setLoading(false);
        }
      },
    );
  };

  const removeQuoteOffering = async (
    payload: IQuoteOfferingRemoveReqSchema,
  ) => {
    return performAsyncAction<IQuoteRespSchema | null>(
      {
        operation: 'remove',
        quoteId,
        quoteOfferingId: payload.target_id,
      },
      async () => {
        try {
          setLoading(true);
          const res = await doRemoveQuoteOffering(quoteId, payload);

          queryClient.invalidateQueries({
            queryKey: cpqServiceQueryKeys.quotesById(quoteId),
          });
          queryClient.invalidateQueries({
            queryKey: cpqServiceQueryKeys.quotePrices(quoteId),
          });
          queryClient.invalidateQueries({
            queryKey: cpqServiceQueryKeys.quoteBillingSchedule(quoteId),
          });
          queryClient.invalidateQueries({
            queryKey: cpqServiceQueryKeys.quoteTemplateHtml(quoteId),
          });
          return res;
        } catch (error) {
          handleApiErrorToast(error);
          return null;
        } finally {
          setLoading(false);
        }
      },
    );
  };

  return {
    loading,
    createOfferingLoading,
    quoteOffering,
    createQuoteOffering,
    updateQuoteOffering,
    revertQuoteOffering,
    removeQuoteOffering,
  };
};

export default useQuoteOffering;
